/*
 * Medico accessibility runtime (2026-09-12)
 *
 * Two passes, both idempotent and idle-scheduled so they never compete with
 * first paint:
 *
 *   fixContrast  - measures every text element against its REAL composited
 *                  background and re-tints only the ones below WCAG AA.
 *                  This is what covers third-party markup (Judge.me) and
 *                  AI-generated blocks that a stylesheet cannot target
 *                  without breaking their light-on-dark variants.
 *   nameControls - gives an accessible name to controls, fields, iframes,
 *                  progress bars and scroll regions that ship without one.
 *
 * Labels come from data-* attributes on the script tag so the strings stay
 * translatable in Liquid.
 */
(function () {
  "use strict";
  if (window.__medicoA11y) return;
  window.__medicoA11y = 1;

  var TARGET = 4.75; /* aim above 4.5 so rounding never fails an audit */
  var LARGE = 3.2;

  /* Elements whose colour is a deliberate design decision and must not be
     re-tinted. The promo badge is white-on-translucent-teal by request; the
     contrast pass used to darken it a moment after load, which read as the
     lettering changing colour and growing a shadow. */
  var CONTRAST_EXEMPT = ".medico-promo-badge, .medico-discount-badge";

  var cfg = document.currentScript || document.querySelector("script[data-medico-a11y]");
  function opt(name, fallback) {
    var v = cfg && cfg.getAttribute("data-" + name);
    return v || fallback;
  }
  var GENERIC_ADD = opt("add", "Add to cart");
  var GENERIC_QTY = opt("qty", "Quantity");
  var GENERIC_REGION = opt("region", "carousel");
  var GENERIC_PROGRESS = opt("progress", "progress");
  var GENERIC_REVIEWS = opt("reviews", "reviews");

  /* ---------------- colour maths ---------------- */
  function parse(c) {
    var m = /rgba?\(([^)]+)\)/.exec(c || "");
    if (!m) return null;
    var p = m[1].split(/[\s,\/]+/).filter(Boolean).map(parseFloat);
    if (p.length < 3) return null;
    return { r: p[0], g: p[1], b: p[2], a: p.length > 3 ? p[3] : 1 };
  }
  function lum(c) {
    function f(v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }
    return 0.2126 * f(c.r) + 0.7152 * f(c.g) + 0.0722 * f(c.b);
  }
  function ratio(a, b) {
    var l1 = lum(a), l2 = lum(b);
    if (l1 < l2) { var t = l1; l1 = l2; l2 = t; }
    return (l1 + 0.05) / (l2 + 0.05);
  }
  function over(fg, bg) {
    var a = fg.a;
    return { r: fg.r * a + bg.r * (1 - a), g: fg.g * a + bg.g * (1 - a), b: fg.b * a + bg.b * (1 - a), a: 1 };
  }
  function scale(c, k) {
    return {
      r: Math.max(0, Math.min(255, c.r * k)),
      g: Math.max(0, Math.min(255, c.g * k)),
      b: Math.max(0, Math.min(255, c.b * k)),
      a: 1
    };
  }
  function css(c) {
    return "rgb(" + Math.round(c.r) + ", " + Math.round(c.g) + ", " + Math.round(c.b) + ")";
  }

  function effectiveBg(el) {
    var node = el, acc = null;
    while (node && node.nodeType === 1) {
      var bg = parse(window.getComputedStyle(node).backgroundColor);
      if (bg && bg.a > 0) {
        if (!acc) acc = bg;
        if (bg.a >= 1) return acc.a >= 1 ? acc : over(acc, bg);
      }
      node = node.parentElement;
    }
    var white = { r: 255, g: 255, b: 255, a: 1 };
    return acc ? over(acc, white) : white;
  }

  function hasOwnText(el) {
    for (var i = 0; i < el.childNodes.length; i++) {
      var n = el.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue && n.nodeValue.trim()) return true;
    }
    return false;
  }

  function fixContrast(root) {
    var els = root.querySelectorAll ? root.querySelectorAll("body *") : [];
    var checked = 0;
    for (var i = 0; i < els.length && checked < 4000; i++) {
      var el = els[i];
      if (el.__contrastDone) continue;
      if (!hasOwnText(el)) continue;

      /* hands off the badges - and never look at them again */
      if (el.closest && el.closest(CONTRAST_EXEMPT)) { el.__contrastDone = 1; continue; }

      var cs = window.getComputedStyle(el);
      /* an element that is not rendered yet must stay eligible for a later
         pass - marking it done here is what let late widgets slip through */
      if (cs.visibility === "hidden" || cs.display === "none") continue;
      if (parseFloat(cs.opacity) === 0) continue;
      if (!el.getClientRects().length) continue;

      checked++;
      el.__contrastDone = 1;

      var fg = parse(cs.color);
      if (!fg) continue;
      var bg = effectiveBg(el);
      if (fg.a < 1) fg = over(fg, bg);

      var size = parseFloat(cs.fontSize) || 16;
      var bold = (parseInt(cs.fontWeight, 10) || 400) >= 700;
      var need = (size >= 24 || (bold && size >= 18.66)) ? LARGE : TARGET;
      if (ratio(fg, bg) >= need) continue;

      var darker = lum(bg) > 0.4;
      var best = null;
      for (var s = 1; s <= 20; s++) {
        var cand = scale(fg, darker ? 1 - s * 0.05 : 1 + s * 0.12);
        if (ratio(cand, bg) >= need) { best = cand; break; }
      }
      if (!best) best = darker ? { r: 0, g: 0, b: 0, a: 1 } : { r: 255, g: 255, b: 255, a: 1 };
      el.style.setProperty("color", css(best), "important");
    }
  }

  /* ---------------- accessible names ---------------- */
  function textOf(el) {
    if (!el) return "";
    return (el.textContent || "").replace(/\s+/g, " ").trim();
  }
  function esc(v) { try { return CSS.escape(v); } catch (e) { return null; } }

  function hasName(el) {
    if (el.getAttribute("aria-label")) return true;
    if (el.getAttribute("aria-labelledby")) return true;
    if (el.getAttribute("title")) return true;
    if (textOf(el)) return true;
    if (el.querySelector && el.querySelector("img[alt]:not([alt=''])")) return true;
    var id = el.id ? esc(el.id) : null;
    if (id && document.querySelector('label[for="' + id + '"]')) return true;
    if (el.closest && el.closest("label")) return true;
    return false;
  }

  function cardTitle(el) {
    var card = el.closest && el.closest(".card-wrapper, .card, .grid__item, li");
    if (!card) return "";
    return textOf(card.querySelector(".card__heading a, .card__heading, h3 a, h3")).slice(0, 80);
  }

  function nameControls(root) {
    if (!root || !root.querySelectorAll) return;

    var buttons = root.querySelectorAll('button, [role="button"], summary');
    for (var i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      var svgs = b.querySelectorAll("svg");
      for (var s = 0; s < svgs.length; s++) {
        if (!svgs[s].querySelector("title")) {
          svgs[s].setAttribute("aria-hidden", "true");
          svgs[s].setAttribute("focusable", "false");
        }
      }
      if (hasName(b)) continue;
      var label = "";
      if (b.id && b.id.indexOf("direct-add-btn") === 0) {
        var t = cardTitle(b);
        label = t ? GENERIC_ADD + " - " + t : GENERIC_ADD;
      } else if (b.className && /qty|quantity/i.test(String(b.className))) {
        label = GENERIC_QTY;
      } else {
        var svgTitle = b.querySelector("svg title");
        label = svgTitle ? textOf(svgTitle) : (b.getAttribute("name") || b.getAttribute("data-action") || "");
      }
      if (label) b.setAttribute("aria-label", label);
    }

    var links = root.querySelectorAll("a[href]");
    for (var a = 0; a < links.length; a++) {
      var lk = links[a];
      if (hasName(lk)) continue;
      if (/jdgm|review/i.test(String(lk.className || ""))) {
        lk.setAttribute("aria-label", GENERIC_REVIEWS);
        continue;
      }
      var im = lk.querySelector("img");
      if (im) { lk.setAttribute("aria-label", im.getAttribute("alt") || lk.getAttribute("href")); continue; }
      var st = lk.querySelector("svg title");
      if (st) lk.setAttribute("aria-label", textOf(st));
    }

    var fields = root.querySelectorAll("input:not([type=hidden]), select, textarea");
    for (var k = 0; k < fields.length; k++) {
      var f = fields[k];
      if (hasName(f)) continue;
      var fl = f.getAttribute("placeholder") || f.getAttribute("name") || "";
      if (f.id && f.id.indexOf("qty-") === 0) {
        var ct = cardTitle(f);
        fl = ct ? GENERIC_QTY + " - " + ct : GENERIC_QTY;
      }
      if (fl) f.setAttribute("aria-label", fl);
    }

    var frames = root.querySelectorAll("iframe:not([title])");
    for (var n = 0; n < frames.length; n++) {
      frames[n].setAttribute("title", frames[n].getAttribute("name") || "embedded content");
    }

    var bars = root.querySelectorAll('[role="progressbar"]');
    for (var pb = 0; pb < bars.length; pb++) {
      if (!hasName(bars[pb])) bars[pb].setAttribute("aria-label", GENERIC_PROGRESS);
    }

    var tracks = root.querySelectorAll('.slider, [id^="Slider-"], .slideshow__slides, [class*="carousel"], [class*="scroller"]');
    for (var r = 0; r < tracks.length; r++) {
      var tr = tracks[r];
      if (tr.hasAttribute("tabindex")) continue;
      if (tr.scrollWidth <= tr.clientWidth + 4 && tr.scrollHeight <= tr.clientHeight + 4) continue;
      var ov = window.getComputedStyle(tr);
      if (!/auto|scroll/.test(ov.overflowX + ov.overflowY)) continue;
      tr.setAttribute("tabindex", "0");
      if (!tr.getAttribute("role")) tr.setAttribute("role", "region");
      if (!hasName(tr)) tr.setAttribute("aria-label", GENERIC_REGION);
    }
  }

  function run() {
    try { nameControls(document); } catch (e) {}
    try { fixContrast(document); } catch (e) {}
  }

  function idle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 1000 });
    else setTimeout(fn, 150);
  }

  if (document.readyState !== "loading") idle(run);
  document.addEventListener("DOMContentLoaded", function () { idle(run); });
  window.addEventListener("load", function () { idle(run); setTimeout(run, 1500); });

  if ("MutationObserver" in window) {
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = null; idle(run); }, 400);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
